import {
  useMutation,
  useQueryClient,
  type QueryKey,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query'

/**
 * One write, wired so the UI never shows the value the user just replaced.
 *
 * Every mutation in this app used to be `mutationFn` + a fire-and-forget
 * `invalidateQueries` in `onSuccess`. That combination produces the flicker the
 * whole module exists to remove, in two steps:
 *
 *  1. `invalidateQueries` returns a promise that nobody awaited, so `mutateAsync`
 *     resolved as soon as the *write* finished — while the *read* that proves it
 *     was still in flight. Callers that `await mutateAsync(...)` and then close a
 *     sheet handed control back over a cache that still held the old row.
 *  2. React Query keeps serving the previous data during a refetch. So the list
 *     underneath rendered the old value once more, then snapped to the new one
 *     when the refetch landed — the "flip between new/old" itself.
 *
 * `useOptimisticMutation` closes both:
 *
 *  - `optimistic` patches the cache in `onMutate`, so the new value is on screen
 *    on the next frame rather than after a round trip, and is rolled back from a
 *    snapshot if the server refuses.
 *  - `invalidateKeys` is invalidated from `onSettled` and the promise is
 *    **returned**, so the mutation stays pending until the refetch has landed.
 *    Query-core awaits `options.onSettled` inside the same `execute()` call that
 *    resolves `mutateAsync`, so `await mutateAsync(...)` now means "the caches
 *    listed here agree with the server", which is what calling code assumed all
 *    along.
 *
 * A mutation wants `optimistic` when the next value is knowable on the client
 * (a renamed row, a deleted one, a toggled status). When it is not — an import,
 * a bulk re-weight, anything the server computes — `invalidateKeys` alone is
 * still correct and still flicker-free, because the awaited refetch means the
 * caller never sees the in-between state.
 */

/** A cache entry, or family of entries, to repaint the moment a mutation starts. */
export interface CachePatch {
  queryKey: QueryKey
  exact: boolean
  apply: (previous: unknown) => unknown
}

/**
 * Build a typed patch over one cache key.
 *
 * `updater` receives the cached value for a single matched entry. Returning
 * `undefined` leaves that entry untouched — the same no-op contract
 * `setQueryData` itself uses — and entries that have never been fetched are
 * skipped outright rather than seeded, so a patch can never invent a list the
 * user has not loaded.
 *
 * The mutation's variables are not passed in: `optimistic` is a closure over
 * them already, so reading them from the enclosing scope keeps this to a single
 * type parameter and the call sites to a single line.
 *
 * The key is matched as a **prefix** by default, which is what most callers want:
 * one patch covers `['x', id]` for every id currently cached. Pass
 * `{ exact: true }` to pin it to a single entry.
 */
export function cachePatch<TData>(
  queryKey: QueryKey,
  updater: (previous: TData) => TData | undefined,
  options: { exact?: boolean } = {}
): CachePatch {
  return {
    queryKey,
    exact: options.exact ?? false,
    apply: (previous) => (previous === undefined ? undefined : updater(previous as TData)),
  }
}

/**
 * What `onMutate` hands to `onError` — query-core calls this the mutation's
 * `TOnMutateResult`.
 */
interface OptimisticSnapshot {
  /** Pre-mutation values for every entry the patches touched, for rollback. */
  snapshots: Array<[QueryKey, unknown]>
}

export interface OptimisticMutationOptions<TData, TError, TVariables>
  extends Omit<
    UseMutationOptions<TData, TError, TVariables, OptimisticSnapshot>,
    'onMutate' | 'onSettled'
  > {
  /**
   * Caches to resync once the write settles. Awaited, so the mutation is not
   * "done" until these agree with the server. A function form can key off the
   * variables and the server's response.
   */
  invalidateKeys?:
    | QueryKey[]
    | ((variables: TVariables, data: TData | undefined) => QueryKey[])
  /** Caches to repaint immediately, before the server has answered. */
  optimistic?: (variables: TVariables) => CachePatch[]
}

export function useOptimisticMutation<TData = unknown, TError = Error, TVariables = void>({
  invalidateKeys,
  optimistic,
  onError,
  ...options
}: OptimisticMutationOptions<TData, TError, TVariables>): UseMutationResult<
  TData,
  TError,
  TVariables,
  OptimisticSnapshot
> {
  const queryClient = useQueryClient()

  return useMutation<TData, TError, TVariables, OptimisticSnapshot>({
    ...options,

    onMutate: async (variables) => {
      const patches = optimistic?.(variables) ?? []
      const snapshots: Array<[QueryKey, unknown]> = []

      for (const patch of patches) {
        const filters = { queryKey: patch.queryKey, exact: patch.exact }
        // A read already in flight would land *after* the patch and repaint the
        // old value — the flicker, arriving by a different route. Cancel first.
        await queryClient.cancelQueries(filters)
        snapshots.push(...queryClient.getQueriesData(filters))
        queryClient.setQueriesData(filters, (previous: unknown) => patch.apply(previous))
      }

      return { snapshots }
    },

    onError: (error, variables, snapshot, context) => {
      for (const [queryKey, previous] of snapshot?.snapshots ?? []) {
        queryClient.setQueryData(queryKey, previous)
      }
      return onError?.(error, variables, snapshot, context)
    },

    // Returned, not fired and forgotten: this promise is what makes
    // `await mutateAsync(...)` mean "the UI is telling the truth now".
    onSettled: (data, _error, variables) => {
      const keys =
        typeof invalidateKeys === 'function'
          ? invalidateKeys(variables, data)
          : (invalidateKeys ?? [])
      return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
  })
}

/**
 * The two list edits that come up in nearly every admin CRUD hook.
 *
 * They exist so a caller writes the *intent* ("this row is gone") rather than a
 * fresh `filter`/`map` each time, and so the id field is named once per hook.
 */
export const listPatch = {
  /** Drop a row from a cached array. */
  remove<T extends { id: string }>(items: T[], id: string): T[] {
    return items.filter((item) => item.id !== id)
  },

  /** Merge a partial update into one row of a cached array. */
  update<T extends { id: string }>(items: T[], id: string, changes: Partial<T>): T[] {
    return items.map((item) => (item.id === id ? { ...item, ...changes } : item))
  },
}
